/**
 * Console pattern: caller/Preferences — 偏好设置.
 *
 * Spec §5.3. Two regions:
 *   1. GlobalPolicyCard — mode 选择 + 三计数 + 「打开名单管理」跳转
 *      切到 allow_all = destructive，弹 ModeChangeImpact AlertDialog。
 *   2. 自动路由偏好 — task_type → hotline_id 映射表 (CRUD).
 *
 * 静态原型：mode 切换会弹一个内联确认横幅而非真 AlertDialog（避免引入
 * 新的 UI 依赖，但 tone 保留）。
 */

import { useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Plus,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";

import {
  ConsolePageHeader,
  ConsoleCard,
  ConsoleButton,
} from "../shells/console-shell";
import { cn } from "@/app/components/ui/utils";

type Mode = "manual" | "allow_listed" | "allow_all";

const MODE_INFO: Record<Mode, { label: string; oneLine: string; tone: "warn" | "ok" | "danger" }> = {
  manual: {
    label: "manual",
    oneLine: "每一次调用都需要你点一下「批准」。最稳，最累。",
    tone: "warn",
  },
  allow_listed: {
    label: "allow_listed",
    oneLine: "只有进入白名单的 Responder/Hotline 才放行；其余仍需逐次批准。",
    tone: "ok",
  },
  allow_all: {
    label: "allow_all",
    oneLine: "所有调用直接放行，仅黑名单拦截。仅用于完全可信环境。",
    tone: "danger",
  },
};

type ModeChangeImpact = {
  destructive: boolean;
  bullets: { tone: "danger" | "warn" | "good" | "neutral"; text: string }[];
  confirmLabel: string;
};

function impactOf(from: Mode, to: Mode): ModeChangeImpact {
  if (to === "allow_all") {
    return {
      destructive: true,
      bullets: [
        { tone: "danger", text: "所有未在黑名单的 Hotline 调用都将被立刻放行，包括支付、删除、查询数据库等高风险动作" },
        { tone: "warn", text: "Agent 一旦被注入恶意指令，没有审批兜底" },
        { tone: "neutral", text: "黑名单仍会生效；可以先把高危 hotline 加进黑名单再切" },
      ],
      confirmLabel: "我已确认风险，切换到 allow_all",
    };
  }
  if (to === "manual") {
    return {
      destructive: false,
      bullets: [
        { tone: "good", text: "回到最稳模式：每次调用都需要你确认" },
        { tone: "warn", text: "若 Agent 调用频次较高，每天可能堆积几十条审批" },
        from === "allow_all"
          ? { tone: "neutral", text: "白名单不会丢，重新切回 allow_listed 即可恢复" }
          : { tone: "neutral", text: "已存在的白名单条目会保留但不再生效" },
      ],
      confirmLabel: "切换到 manual",
    };
  }
  return {
    destructive: false,
    bullets: [
      { tone: "good", text: "白名单上的 Responder/Hotline 直接放行，其余仍需审批" },
      { tone: "neutral", text: "可以在「名单管理」按 Responder 整体放行，或精确到 hotline_id" },
      from === "allow_all"
        ? { tone: "warn", text: "不在白名单里的调用从「直接执行」变成「等待审批」" }
        : { tone: "good", text: "审批工作量预计下降约 70%（基于过去 7 天调用画像）" },
    ],
    confirmLabel: "切换到 allow_listed",
  };
}

const COUNTS = {
  responderWhitelist: 3,
  hotlineWhitelist: 7,
  blocklist: 1,
};

type RouteRule = {
  taskType: string;
  hotlineId: string;
  enabled: boolean;
};

const INIT_ROUTES: RouteRule[] = [
  { taskType: "search-the-web", hotlineId: "official.web-search.bing.v1", enabled: true },
  { taskType: "summarize-document", hotlineId: "local.workspace-summary.v1", enabled: true },
  { taskType: "send-notification", hotlineId: "ops.notify@v1", enabled: false },
];

export function ConsolePagePreferences() {
  const [mode, setMode] = useState<Mode>("allow_listed");
  const [pendingMode, setPendingMode] = useState<Mode | null>(null);
  const [routes, setRoutes] = useState<RouteRule[]>(INIT_ROUTES);

  const tryChangeMode = (to: Mode) => {
    if (to === mode) return;
    setPendingMode(to);
  };
  const confirmModeChange = () => {
    if (pendingMode) setMode(pendingMode);
    setPendingMode(null);
  };

  return (
    <>
      <ConsolePageHeader
        kicker="CALLER · 偏好设置"
        title="审批策略 · 自动路由"
        description="原型展示策略 region；改动只存在本机，AlertDialog 用内联确认横幅替代以保持视觉简洁。"
      />

      {/* 1. Global policy card */}
      <ConsoleCard className="mb-5">
        <div className="flex items-start gap-3 mb-4">
          <ShieldCheck className="h-5 w-5 mt-0.5 text-[var(--brand-teal)] shrink-0" strokeWidth={2.5} />
          <div className="flex-1 min-w-0">
            <p className="text-[14px] font-semibold">全局审批策略</p>
            <p className="text-[12px] text-[var(--brand-muted)] mt-0.5">
              三选一。任何时候 Caller 收到调用请求，先按这条规则决定是否需要逐次审批。
            </p>
          </div>
          <span className="c-status c-status-info text-[10px]">
            当前 · {MODE_INFO[mode].label}
          </span>
        </div>

        {/* mode picker */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {(Object.keys(MODE_INFO) as Mode[]).map((m) => {
            const info = MODE_INFO[m];
            const active = mode === m;
            return (
              <button
                key={m}
                onClick={() => tryChangeMode(m)}
                className={cn(
                  "text-left p-3 border bg-white",
                  active
                    ? "c-ink-border-2 c-shadow-1"
                    : "border-[var(--border)] hover:border-[var(--brand-muted)]",
                )}
                style={{ borderRadius: "var(--c-radius)" }}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-mono text-[12.5px] font-semibold">{info.label}</span>
                  {info.tone === "danger" && (
                    <AlertTriangle className="h-3 w-3 text-[var(--c-status-error-fg)]" strokeWidth={2.5} />
                  )}
                </div>
                <p className="text-[11.5px] text-[var(--brand-muted)] leading-snug">
                  {info.oneLine}
                </p>
              </button>
            );
          })}
        </div>

        {/* inline impact confirmation (替代 AlertDialog) */}
        {pendingMode && (
          <ModeImpactBanner
            from={mode}
            to={pendingMode}
            onCancel={() => setPendingMode(null)}
            onConfirm={confirmModeChange}
          />
        )}

        {/* counts + jump to AccessLists */}
        <div className="mt-2 pt-4 border-t flex items-center gap-6" style={{ borderColor: "var(--border)" }}>
          <CountChip label="Responder 白名单" count={COUNTS.responderWhitelist} />
          <CountChip label="Hotline 白名单" count={COUNTS.hotlineWhitelist} />
          <CountChip label="黑名单" count={COUNTS.blocklist} tone="error" />
          <Link
            to="/console/access-lists?from=preferences"
            className="ml-auto text-[12px] font-semibold text-[var(--ink)] inline-flex items-center gap-1 hover:text-[var(--brand-orange)]"
          >
            打开名单管理
            <ArrowRight className="h-3 w-3" strokeWidth={2.5} />
          </Link>
        </div>
      </ConsoleCard>

      {/* 2. Auto-routing rules */}
      <ConsoleCard padded={false}>
        <div className="px-5 py-3.5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <p className="text-[13px] font-semibold">自动路由偏好</p>
            <p className="text-[11.5px] text-[var(--brand-muted)] mt-0.5">
              当 Agent 给出 task_type 时自动选定 hotline_id；命中规则后跳过目录搜索。
            </p>
          </div>
          <ConsoleButton variant="ghost">
            <Plus className="h-3.5 w-3.5" />
            新增规则
          </ConsoleButton>
        </div>

        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left bg-[var(--brand-secondary)]/30 border-b" style={{ borderColor: "var(--border)" }}>
              <th className="px-5 py-2 font-semibold text-[var(--brand-muted)] uppercase text-[10.5px] tracking-wider">task_type</th>
              <th className="px-5 py-2 font-semibold text-[var(--brand-muted)] uppercase text-[10.5px] tracking-wider">绑定 hotline_id</th>
              <th className="px-5 py-2 font-semibold text-[var(--brand-muted)] uppercase text-[10.5px] tracking-wider w-24">启用</th>
              <th className="px-5 py-2 w-12" />
            </tr>
          </thead>
          <tbody>
            {routes.map((r, i) => (
              <tr
                key={r.taskType}
                className={cn(i < routes.length - 1 && "border-b")}
                style={i < routes.length - 1 ? { borderColor: "var(--border)" } : undefined}
              >
                <td className="px-5 py-2.5 font-mono">{r.taskType}</td>
                <td className="px-5 py-2.5 font-mono text-[var(--brand-muted)]">{r.hotlineId}</td>
                <td className="px-5 py-2.5">
                  <span
                    className={cn(
                      "c-status text-[10px]",
                      r.enabled ? "c-status-success" : "c-status-warn",
                    )}
                  >
                    {r.enabled ? "启用" : "停用"}
                  </span>
                </td>
                <td className="px-5 py-2.5 text-right">
                  <button
                    onClick={() => setRoutes(routes.filter((x) => x.taskType !== r.taskType))}
                    className="text-[var(--brand-muted)] hover:text-[var(--c-status-error-fg)]"
                    title="删除"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {routes.length === 0 && (
          <div className="px-5 py-8 text-center text-[12.5px] text-[var(--brand-muted)]">
            还没有自动路由规则。Agent 拿到 task_type 时会回退到 Catalog 搜索 + 你逐次确认。
          </div>
        )}
      </ConsoleCard>
    </>
  );
}

function CountChip({
  label,
  count,
  tone = "neutral",
}: {
  label: string;
  count: number;
  tone?: "neutral" | "error";
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span
        className={cn(
          "text-[18px] font-semibold tabular-nums",
          tone === "error" ? "text-[var(--c-status-error-fg)]" : "text-[var(--ink)]",
        )}
      >
        {count}
      </span>
      <span className="text-[11.5px] text-[var(--brand-muted)]">{label}</span>
    </div>
  );
}

const TONE_CLASS: Record<"danger" | "warn" | "good" | "neutral", string> = {
  danger: "text-[var(--c-status-error-fg)]",
  warn: "text-[var(--c-status-warn-fg)]",
  good: "text-[var(--c-status-success-fg)]",
  neutral: "text-[var(--brand-muted)]",
};

function ModeImpactBanner({
  from,
  to,
  onCancel,
  onConfirm,
}: {
  from: Mode;
  to: Mode;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const impact = impactOf(from, to);
  return (
    <div
      className={cn(
        "p-4 border-l-[3px] mb-1",
        impact.destructive
          ? "bg-[var(--c-status-error-bg)] border-l-[var(--c-status-error-fg)]"
          : "bg-[var(--brand-secondary)]/50 border-l-[var(--brand-ink)]",
      )}
      style={{ borderRadius: "var(--c-radius-sm)" }}
    >
      <div className="flex items-start gap-3">
        {impact.destructive ? (
          <AlertTriangle className="h-4 w-4 mt-0.5 text-[var(--c-status-error-fg)] shrink-0" strokeWidth={2.5} />
        ) : (
          <CheckCircle2 className="h-4 w-4 mt-0.5 text-[var(--ink)] shrink-0" strokeWidth={2.5} />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[12.5px] font-semibold">
            从 <span className="font-mono">{MODE_INFO[from].label}</span> 切到{" "}
            <span className="font-mono">{MODE_INFO[to].label}</span>
            {impact.destructive && (
              <span className="ml-2 text-[10.5px] font-bold uppercase tracking-wider text-[var(--c-status-error-fg)]">
                · destructive
              </span>
            )}
          </p>
          <ul className="mt-2 space-y-1">
            {impact.bullets.map((b, i) => (
              <li key={i} className="text-[12px] flex items-start gap-2">
                <span className={cn("font-bold", TONE_CLASS[b.tone])}>•</span>
                <span className="text-[var(--ink)]/85 leading-snug">{b.text}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex items-center gap-2">
            <ConsoleButton
              variant={impact.destructive ? "danger" : "primary"}
              onClick={onConfirm}
            >
              {impact.confirmLabel}
            </ConsoleButton>
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-[12.5px] font-medium text-[var(--brand-muted)] hover:text-[var(--ink)] inline-flex items-center gap-1"
            >
              <X className="h-3 w-3" strokeWidth={2.5} />
              取消
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
